/**
 * MowWindowPage — `/pulse/mow-window`.
 *
 * Surfaces the "when can I mow?" answer the user pays for. Pulls:
 *   • Current moisture + moisture sparkline (the trigger metric)
 *   • Opens-in / window-length tiles
 *   • Four-check "WHY this window" list
 *   • Cut-low recommendation
 *
 * This is intentionally moisture-driven (≤50% threshold) — separate from the
 * irrigation-watering-window scoring, which is forecast/forward looking. Mow
 * window is about *now*-ish drying conditions for a clean cut.
 */
import React, { useEffect, useState } from 'react';
import { Scissors, CheckCircle2, Brain, Lightbulb } from 'lucide-react';
import { PulseDetailLayout, PulseTile, PulseInsight } from '../../components/pulse/PulseDetailLayout';
import { PulseSparkline } from '../../components/pulse/PulseSparkline';
import { API_URL } from '../../config/api';

export default function MowWindowPage() {
  const [now, setNow] = useState(null);
  const [hist, setHist] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/dashboard/now`, { credentials: 'include' })
      .then(r => r.json()).then(setNow).catch(() => {});
    fetch(`${API_URL}/api/dashboard/history?metric=moisture&hours=48`, { credentials: 'include' })
      .then(r => r.json()).then(setHist).catch(() => {});
  }, []);

  const moisture = now?.metrics?.moisture?.value;

  // Project drying rate from history to find when moisture hits the
  // 45-50% mow band. Same regression idea as MoisturePage, kept local
  // because both pages need it but the model is two lines of math.
  let opensIn = null;
  let length = null;
  if (hist?.series && hist.series.length >= 3 && moisture != null) {
    const recent = hist.series.slice(-24);
    const first = recent[0];
    const last = recent[recent.length - 1];
    const dt = (new Date(last.t) - new Date(first.t)) / 3.6e6;
    const dv = last.v - first.v;
    const rate = dt > 0 ? dv / dt : 0;
    if (rate < 0) {
      const hoursTo50 = (50 - moisture) / rate;
      const hoursTo45 = (45 - moisture) / rate;
      if (hoursTo50 > 0) opensIn = Math.max(0, Math.round(hoursTo50));
      if (hoursTo45 > hoursTo50) length = Math.round(hoursTo45 - hoursTo50);
    }
  }

  let status = { text: 'Closed', color: 'text-rose-400' };
  if (moisture != null) {
    if (moisture >= 45 && moisture <= 50) status = { text: 'Open Now', color: 'text-emerald-400' };
    else if (opensIn != null && opensIn <= 6)   status = { text: 'Opening Soon', color: 'text-yellow-400' };
    else if (moisture > 50)                      status = { text: 'Drying Down', color: 'text-amber-400' };
  }

  return (
    <PulseDetailLayout
      title="Mow Window"
      icon={Scissors}
      iconColor="text-emerald-400"
      bigValue={status.text.toUpperCase()}
      bigValueColor="text-emerald-400"
      badgeText="Trigger: 45–50% moisture"
      badgeColor="text-slate-400"
    >
      <div className="grid grid-cols-2 gap-3">
        <PulseTile
          testid="mow-opens-in-tile"
          label="Opens In"
          value={opensIn != null ? `~${opensIn}` : '—'}
          valueColor="text-slate-100"
          subline="hours"
        />
        <PulseTile
          testid="mow-length-tile"
          label="Window Length"
          value={length != null ? `~${length}` : '—'}
          valueColor="text-slate-100"
          subline="hours"
        />
      </div>

      <PulseInsight tone="emerald" icon={Brain} label="Why this window" testid="mow-why-window">
        <ul className="space-y-1.5 mt-1">
          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/>Moisture will be in ideal range</li>
          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/>Plant will be actively growing</li>
          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/>Cut will be cleaner</li>
          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/>Recovery will be faster</li>
        </ul>
      </PulseInsight>

      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold pl-1">
        Moisture Timeline · 48h
      </div>
      <PulseSparkline metric="moisture" hours={48} idealMin={45} idealMax={50} unit="%" />

      <PulseInsight tone="emerald" icon={Lightbulb} label="Recommendation" testid="mow-recommendation">
        Mow during this window for best results. <b>Cut low (&lt;1")</b> to promote density.
      </PulseInsight>
    </PulseDetailLayout>
  );
}
